import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListChecks, Plus, Trash2, Pencil, ChevronUp, ChevronDown } from 'lucide-react';
import alertService from '../../utils/alert';
import SimpleGanttChart from '../../components/charts/SimpleGanttChart';
import { formatDate } from '../../utils/formatDate';
import {
  fetchActivityTemplates, createActivityTemplate, updateActivityTemplate, deleteActivityTemplate,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import { PAGE, CARD, INPUT, LABEL, BTN_PRIMARY, BTN_SECONDARY, FormModal } from '../../components/crm/ui';

const STEP_TYPES = ['call', 'email', 'whatsapp', 'meeting', 'follow_up', 'custom', 'note', 'other', 'technical'];

const STEP_TYPE_COLORS = {
  call: '#0176D3', email: '#3b82f6', whatsapp: '#25D366', meeting: '#8b5cf6',
  follow_up: '#f59e0b', note: '#f59e0b', custom: '#94a3b8', other: '#94a3b8', technical: '#64748b',
};

// A template has no absolute dates — only day-offsets relative to whenever
// it's applied — so this projects steps onto a fake "Day 0" baseline purely
// to preview relative spacing/order, not a real calendar.
function stepsToPreviewRows(steps) {
  const baseline = new Date(2000, 0, 1);
  return steps.map((step, idx) => {
    const start = new Date(baseline.getTime() + step.offsetDays * 86400000);
    const end = new Date(start.getTime() + 86400000); // 1-day marker — no duration field exists to size it by
    return {
      id: idx,
      label: step.title || `Step ${idx + 1}`,
      segments: [{ start, end, color: STEP_TYPE_COLORS[step.type] || '#94a3b8', tooltip: `Day ${step.offsetDays}: ${step.title}` }],
    };
  });
}
const STEP_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const BLANK_TEMPLATE = { name: '', description: '', steps: [] };
const BLANK_STEP = { title: '', description: '', type: 'follow_up', priority: 'medium', offsetDays: 0 };

function ActivityTemplateForm({ template, onClose }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => (template
    ? { ...template, steps: (template.steps || []).map((s) => ({ ...s })) }
    : { ...BLANK_TEMPLATE, steps: [] }));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const addStep = () => set({ steps: [...form.steps, { ...BLANK_STEP }] });
  const removeStep = (idx) => set({ steps: form.steps.filter((_, i) => i !== idx) });
  const updateStep = (idx, field, value) => set({ steps: form.steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s)) });
  const moveStep = (idx, direction) => {
    const steps = [...form.steps];
    const target = idx + direction;
    if (target < 0 || target >= steps.length) return;
    [steps[idx], steps[target]] = [steps[target], steps[idx]];
    set({ steps });
  };

  const save = async () => {
    if (!form.name.trim()) return alertService.error('Error', 'Template name is required');
    if (form.steps.some((s) => !s.title.trim())) return alertService.error('Error', 'Every step needs a title');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        steps: form.steps.map((s, i) => ({ ...s, order: i })),
      };
      if (template) {
        await dispatch(updateActivityTemplate({ id: template._id, data: payload })).unwrap();
        alertService.toast('success', 'Template updated');
      } else {
        await dispatch(createActivityTemplate(payload)).unwrap();
        alertService.toast('success', 'Template created');
      }
      onClose();
    } catch (err) {
      alertService.error('Error', err || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={template ? `Edit ${(template.name || '').trim()}` : 'New activity template'} onClose={onClose} onSubmit={save} saving={saving} submitLabel="Save template">
      <p className="text-[11px] text-[#706E6B]">Define a checklist of tasks that auto-creates when applied to a Lead, Deal, or Contact.</p>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={LABEL}>Template name *</label>
          <input aria-label="Template name" required className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. New Lead Onboarding" />
        </div>
        <div>
          <label className={LABEL}>Description</label>
          <input aria-label="Description" className={INPUT} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Optional description" />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide">Steps ({form.steps.length})</h3>
          <button type="button" onClick={addStep} className={BTN_SECONDARY}><Plus size={14} /> Add step</button>
        </div>

        {form.steps.map((step, idx) => (
          <div key={idx} className="border border-[#D8DDE6] rounded p-3 sm:p-4 space-y-3 bg-[#F4F6F9]">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 shrink-0 mt-1.5 rounded bg-white border border-[#D8DDE6] flex items-center justify-center text-[11px] font-bold text-[#706E6B]">{idx + 1}</span>
              <input aria-label={`Step ${idx + 1} title`} className={`${INPUT} flex-1`} value={step.title} onChange={(e) => updateStep(idx, 'title', e.target.value)} placeholder="Step title" />
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => moveStep(idx, -1)} disabled={idx === 0} aria-label="Move up" title="Move up" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] disabled:opacity-30"><ChevronUp size={16} /></button>
                <button type="button" onClick={() => moveStep(idx, 1)} disabled={idx === form.steps.length - 1} aria-label="Move down" title="Move down" className="p-1.5 text-[#706E6B] hover:text-[#0176D3] disabled:opacity-30"><ChevronDown size={16} /></button>
                <button type="button" onClick={() => removeStep(idx)} aria-label="Remove step" title="Remove step" className="p-1.5 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#F3F2F2] rounded"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:pl-9">
              <select aria-label="Step type" className={INPUT} value={step.type} onChange={(e) => updateStep(idx, 'type', e.target.value)}>
                {STEP_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
              <select aria-label="Step priority" className={INPUT} value={step.priority} onChange={(e) => updateStep(idx, 'priority', e.target.value)}>
                {STEP_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <input aria-label="Days after apply" type="number" min="0" className={`${INPUT} w-20`} value={step.offsetDays} onChange={(e) => updateStep(idx, 'offsetDays', parseInt(e.target.value, 10) || 0)} />
                <span className="text-[11px] font-semibold text-[#706E6B] uppercase whitespace-nowrap">days after apply</span>
              </div>
            </div>
          </div>
        ))}

        {form.steps.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-[#D8DDE6] rounded text-[12px] font-medium text-[#706E6B]">
            No steps yet — click "Add step" to build the checklist.
          </div>
        )}
      </section>

      {form.steps.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide">Preview timeline</h3>
          <p className="text-[11px] text-[#706E6B]">A relative preview — "Day 0" is whenever this template gets applied to a Lead, Deal, or Contact, not a real date.</p>
          <SimpleGanttChart rows={stepsToPreviewRows(form.steps)} />
        </section>
      )}
    </FormModal>
  );
}

export default function ActivityTemplatesPage() {
  const dispatch = useDispatch();
  const { activityTemplates } = useSelector((state) => state.crm);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | template

  useEffect(() => {
    dispatch(fetchActivityTemplates()).finally(() => setLoading(false));
  }, [dispatch]);

  const handleDelete = async (tmpl) => {
    const confirmed = await alertService.confirm('Delete Template?', `Permanently delete "${tmpl.name}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteActivityTemplate(tmpl._id)).unwrap();
      alertService.toast('success', 'Template deleted');
    } catch (err) {
      alertService.error('Error', err || 'Failed to delete template');
    }
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Activity Templates"
        subtitle="Reusable task checklists you can apply to any Lead, Deal, or Contact."
        icon={ListChecks}
        actions={<button type="button" onClick={() => setEditing('new')} className={BTN_PRIMARY}><Plus size={15} /> New Template</button>}
      />

      {loading || activityTemplates.length === 0 ? (
        <div className={`${CARD} px-4 py-12 text-center text-[#706E6B] text-[13px]`}>
          {loading ? 'Loading…' : 'No activity templates yet. Add your first checklist.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {activityTemplates.map((tmpl) => (
            <div key={tmpl._id} className={`${CARD} p-5 flex flex-col justify-between hover:border-[#0176D3] transition-colors`}>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="sf-badge sf-badge-manager">{(tmpl.steps || []).length} steps</span>
                  <span className="text-[10px] text-[#706E6B] font-medium">Updated {formatDate(tmpl.updatedAt)}</span>
                </div>
                <h3 className="font-semibold text-[#2E2E2E] break-words">{tmpl.name}</h3>
                {tmpl.description && <p className="text-[12px] text-[#706E6B] line-clamp-2">{tmpl.description}</p>}
              </div>
              <div className="pt-3 border-t border-[#D8DDE6] flex justify-end gap-1 mt-5">
                <button type="button" onClick={() => setEditing(tmpl)} aria-label={`Edit ${tmpl.name}`} title={`Edit ${tmpl.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={14} /></button>
                <button type="button" onClick={() => handleDelete(tmpl)} aria-label={`Delete ${tmpl.name}`} title={`Delete ${tmpl.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ActivityTemplateForm template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
